import React, { useState } from 'react';
import messages from './Messages';
import Select from 'components/Select';
import Button from 'components/Button/Button';
import { notifyError } from 'components/Notify/Notify';
import {
  ICompanyRole,
  IProjectMemberView,
} from 'store/companies/companiesTypes';
import cloneDeep from 'lodash/cloneDeep';

interface ProjectMemberMigrateProps {
  member: IProjectMemberView;
  roles: { [key: number]: ICompanyRole };
  roleMembers: { [key: string]: IProjectMemberView };
  userRoles: ICompanyRole['roles'];
  addMember: (projectMember: IProjectMemberView) => Promise<IProjectMemberView>;
  updateMember: (
    projectMember: IProjectMemberView
  ) => Promise<IProjectMemberView>;
}

type Action =
  | 'moveToImplicit'
  | 'matchRoleAndMoveToImplicit'
  | 'createAndMoveToImplicit';
export const ProjectMemberMigrate: React.FC<ProjectMemberMigrateProps> = ({
  member,
  roleMembers,
  userRoles,
  roles,
  addMember,
  updateMember,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState(member.relatedRoleId);

  const [loading, setLoading] = useState(false);

  const foundRole = roleMembers[selectedRoleId!];

  const performAction = async (action: Action) => {
    setLoading(true);
    function makeSelfImplicit() {
      const self = cloneDeep(member) as IProjectMemberView;
      if (!self.implicit) {
        self.implicit = true;
        self.admin = false;
        self.visibleAllTasks = false;
        self.canCreate = false;
        updateMember(self).then(
          () => {
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    }
    if (
      action === 'matchRoleAndMoveToImplicit' ||
      action === 'createAndMoveToImplicit'
    ) {
      const result = cloneDeep(member);
      result.userId = undefined;
      result.roleId = selectedRoleId;
      result.relatedRoleId = undefined;
      const actionFunc =
        action === 'matchRoleAndMoveToImplicit' ? updateMember : addMember;
      actionFunc(result).then(
        () => {
          makeSelfImplicit();
        },
        (e) => {
          notifyError(e);
          setLoading(false);
        }
      );
    } else {
      makeSelfImplicit();
    }
  };
  let description = messages.pleaseSelectARole;
  let status: 'ok' | 'warn' | 'error' = 'ok';
  let action: Action | undefined = undefined;
  if (foundRole) {
    description = messages.roleExistInProject;
    const foundRoleObj = foundRole;
    const memberObj = member as IProjectMemberView;
    if (foundRoleObj.title !== memberObj.title) {
      description =
        description + '\n ' + messages.customTitle + messages.differ;
      status = 'warn';
    }
    if (foundRoleObj.canCreate !== memberObj.canCreate) {
      description = description + '\n ' + messages.canCreate + messages.differ;
      status = 'warn';
    }
    if (foundRoleObj.visibleAllTasks !== memberObj.visibleAllTasks) {
      description =
        description + '\n ' + messages.visibleAllTasks + messages.differ;
      status = 'warn';
    }
    if (foundRoleObj.admin !== memberObj.admin) {
      description = description + '\n ' + messages.admin + messages.differ;
      status = 'warn';
    }
    if (foundRoleObj.implicit !== memberObj.implicit) {
      description = description + '\n ' + messages.isImplicit + messages.differ;
      status = 'warn';
    }
    if (status === 'warn') action = 'matchRoleAndMoveToImplicit';
    else if (status === 'ok') {
      action = 'moveToImplicit';
    }
  } else if (selectedRoleId) {
    const users = roles[selectedRoleId].users;
    description = (users && messages.moreThan1MemberMatchs(users.length)) || '';
    if (users?.length > 1) {
      status = 'warn';
    }
    action = 'createAndMoveToImplicit';
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--exact-white)',
        padding: '10px',
        border: '1px gray',
      }}
    >
      <h6>{messages.migrateToRole}</h6>
      <Select
        onChangeValues={(values) => {
          setSelectedRoleId(Number(values[0]?.value) || undefined);
        }}
        values={
          selectedRoleId
            ? [
                {
                  value: selectedRoleId,
                  label: roles[selectedRoleId].name,
                },
              ]
            : []
        }
        options={Object.values(userRoles!).map((item) => ({
          value: item.id,
          label: item.name,
        }))}
        placeholder={messages.roleInProject}
        round={true}
        containerStyle={{ marginLeft: '5px', width: '150px' }}
      />
      <div
        style={{
          color:
            status === 'ok' ? 'green' : status === 'warn' ? 'orange' : 'red',
          whiteSpace: 'pre-wrap',
        }}
      >
        {description}
      </div>
      {action && (
        <Button
          loading={loading}
          disabled={!action}
          onClick={() => action && performAction(action)}
        >
          {messages[action]}
        </Button>
      )}
    </div>
  );
};
